import React from "react";
import { userDispatch } from './localStorage';
import routes from './routes.json';
import { Redirect } from 'react-router-dom';
import { AppInstance } from './services';
import { useEffect, useState } from 'react';
import Loader from '../components/Loaders';
import moment, { tz } from 'moment';
import { CONSTANT_TIMEZONE } from './constants';

export const shrinkTextBased = (limit = 0, text = '') => {
  return text.length > limit ? `${text.substring(0, limit)}...` : text;
};

export const PublicRouter: React.FunctionComponent<any> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const getUser = async () => {
      try {
        const __user = await userDispatch({ case: 'GET', key: 'user' });
        if (__user?.email || __user?.id) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        setIsAuthenticated(false);
      }
    };
    getUser();
  }, []);

  return !isAuthenticated ? <>{children}</> : <Redirect to={routes.facilities} />;
};

export const PrivateRouter: React.FunctionComponent<any> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const res = await userDispatch({ case: 'GET', key: 'role' });
        if (res?.roleType === 'guest') {
          setIsAuthenticated(true);
        } else {
          const __user = await userDispatch({ case: 'GET', key: 'user' });
          if (__user?.email || __user?.id) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        }
      } catch (err) {
        setIsAuthenticated(false);
      }
    };
    checkAuthentication();
  }, []);

  if (isAuthenticated === null) {
    return <Loader />;
  }

  return isAuthenticated ? <>{children}</> : <Redirect to={routes.login} />;
};

export const ApiConfigs: (accessToken: string, refreshToken: string, navigate: any) => void = (accessToken: string, refreshToken: string, navigate: any) => {
  if (accessToken) {
    let reqInt = AppInstance.interceptors.request.use((req:any) => {
      req.headers['access-token'] = refreshToken;
      req.headers.Authorization = `${accessToken}`;
      return req;
    });
    let resInt = AppInstance.interceptors.response.use(
      (res:any) => {
        return res;
      },
      async (err:any) => {
        if (err?.response?.status === 402) {
          await userDispatch({ case: 'CLEAR' });
          AppInstance.interceptors.request.eject(reqInt);
          AppInstance.interceptors.response.eject(resInt);
          navigate.push(routes.login);
        }
        return err;
      }
    );
  }
};

export const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>, files: any, types?: string, size?: number) => {
  const typesArr = types?.split(',') || [];
  let mimeType: any = {
    'application/json': 'json',
    'application/pdf': 'pdf',
    'application/zip': 'zip',
    'text/plain': 'plain',
    'text/html': 'html',
    'text/css': 'css',
    'text/csv': 'csv',
    'image/jpeg': 'jpeg',
    'image/png': 'png',
    'image/gif': 'gif',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'video/mp4': 'mp4',
    'video/avi': 'avi',
    'image/*': 'image',
  };
  if (!e.target.files) {
    return { message: 'Please Upload File', success: false };
  }
  const getFile = e.target.files[0];
  const customSize = size ? size : 2097152;

  // File Size Check
  if (getFile?.size > customSize) {
    return { message: `File is larger than ${Math.floor(customSize / (1024 * 1024))}MB`, success: false };
  }
  if (typesArr?.length > 0) {
    const getMatch: Array<string> = typesArr?.filter((items) => {
      if (items === 'image/*') {
        // If the type is 'image/*', check if the file is any image type
        return getFile.type.startsWith('image/');
      }
      return items === getFile.type;
    });

    if (getMatch.length === 0) {
      return {
        message: `Only the ${typesArr.map((items) => {
          return `${mimeType[items] || items} `;
        })} formats are accepted`,
        success: false,
      };
    }
  }

  return { success: true, data: e.target.files[0] };
};

export const toBase64: (file: any) => Promise<unknown> = (file: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const getFirstLetters = (str: string) => {
  const words = str.split(' ');
  let firstLetters;
  if (words.length === 1) {
    firstLetters = words.map((word) => word.slice(0, 1));
  } else {
    firstLetters = words.slice(0, 2).map((word) => word.slice(0, 1));
  }
  return firstLetters.join('');
};

export const returnUserTime = (format: string, date: string, time: string) => {
  return moment(
    moment
      .tz(`${moment(date).format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD h:mm A', CONSTANT_TIMEZONE)
      .clone()
      .tz(tz.guess())
  ).format(format);
};

// facilityDetails?.intervalDuration

export function generateTimeSlots(selectedTime?: string, endTime?: string, interval?: number) {
  const timeSlots: any[] = [];
  const intervalDuration = interval || 30;
  const startTimeMoment = selectedTime
    ? moment(selectedTime, 'h:mm A', true).isValid()
      ? moment(selectedTime, 'h:mm A').add(intervalDuration, 'minutes')
      : moment.parseZone(selectedTime)
    : moment().startOf('day');
  const endTimeMoment = endTime ? moment.parseZone(endTime).subtract(intervalDuration, 'minutes') : moment().endOf('day').subtract(intervalDuration, 'minutes');
  if (!endTime) {
    endTimeMoment.add(intervalDuration, 'minutes');
  }

  let currentTime = startTimeMoment.clone();
  while (currentTime <= endTimeMoment) {
    timeSlots.push(currentTime.format('h:mm A'));
    currentTime.add(intervalDuration, 'minutes');
  }
  return timeSlots;
}


export const addRouteHistory = async (pathname: string) => {
  const isContainsLocation = pathname.includes("location");
  const isContainsCategory = pathname.includes("category");
  const isContainsAccessCard = pathname.includes("/access-card-request");
  if (isContainsCategory && isContainsLocation) {
    await userDispatch({ case: "REMOVE", key: "role" });
    await userDispatch({ case: "ADD", key: "GOBACK", payload: pathname });
  }
  if (isContainsAccessCard) {
    await userDispatch({ case: "REMOVE", key: "role" });
    await userDispatch({ case: "ADD", key: "GOBACK", payload: pathname });
  }
};

/**
 * Calculates the cart count from bookings data
 * Filters items that are in cart or added to card, excluding booked, enquiry, and rejected items
 * Validates date for items not explicitly in cart
 * @param bookingsResult - Array of booking items from API response
 * @returns Number of valid cart items
 */
export const calculateCartCount = (bookingsResult: any[]): number => {
  if (!bookingsResult || !Array.isArray(bookingsResult)) {
    return 0;
  }

  const getCartData = bookingsResult.filter((items: any) => {
    return items?.addedInCard && items?.cartStatus !== "Booked" && items?.type !== "Enquiry" && items?.type !== "Rejected";
  });

  // If item is in cart, always count it regardless of date. Otherwise, validate date
  const validCartItems = getCartData.filter((items: any) => {
    const isInCart = items?.cartStatus === "In Cart";
    if (isInCart) {
      return true; // Always count items in cart
    }
    // For other items, check if startDate is not in the past
    return items?.startDate 
      ? !moment(moment(items?.startDate).format("YYYY-MM-DD hh:mm A")).isBefore(moment().format("YYYY-MM-DD hh:mm A"))
      : true; // If no startDate, still count it
  });

  return validCartItems.length;
};

/**
 * Determines the target pricing type based on contact roles and active status
 * @param contactRoles - The contact role string
 * @param isActive - Whether the user is active
 * @returns The target pricing type or null
 */
export const getTargetPricingType = (contactRoles: string, isActive: boolean): string | null => {
  if (contactRoles === 'Startup_Team_Member') {
    return isActive ? 'Startup' : 'Non Member';
  }
  return null;
};